/**
 * MatchContactPanel — shared slide-over for linking an email to an existing contact.
 * Used by all 4 sync channels (Gmail, Calendar, Amplitude, BigDough).
 *
 * item: { email, name?, firm_guess?, source?, prefill? }
 * onLinked: (email, contact) => void
 * onAddNew: (prefill) => void
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { searchContacts, linkEmailToContact } from '../api/backend.js';

export default function MatchContactPanel({ item, firms = [], getToken, onClose, onLinked, onAddNew }) {
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState(null);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected]   = useState(null);
  const [linking, setLinking]     = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [linked, setLinked]       = useState(false);
  const searchRef                 = useRef(null);
  const debounceRef               = useRef(null);

  const domain = (item.email ?? '').split('@')[1]?.toLowerCase() ?? '';

  const matchedFirm = useMemo(() => {
    if (!domain || !firms.length) return null;
    const root = domain.split('.')[0];
    return firms.find((f) => (f.firm_name ?? '').toLowerCase().includes(root)) ?? null;
  }, [domain, firms]);

  useEffect(() => { setTimeout(() => searchRef.current?.focus(), 80); }, []);

  const doSearch = useCallback((q) => {
    if (!q.trim()) { setResults(null); return; }
    setSearching(true);
    searchContacts(getToken, q)
      .then(setResults)
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, [getToken]);

  const onQueryChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    setSelected(null);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 280);
  };

  const doLink = async () => {
    if (!selected) return;
    setLinking(true);
    setLinkError(null);
    try {
      await linkEmailToContact(getToken, selected.contact_id, item.email);
      setLinked(true);
      setTimeout(() => onLinked(item.email, { ...selected, email: item.email }), 1200);
    } catch (e) {
      setLinkError(e.message);
    } finally {
      setLinking(false);
    }
  };

  const doAddNew = () => {
    onAddNew?.({
      email:     item.email,
      name:      item.name ?? '',
      firm_name: item.firm_guess ?? matchedFirm?.firm_name ?? '',
      firm_id:   matchedFirm?.firm_id ?? '',
      source:    item.source ?? '',
      ...(item.prefill ?? {}),
    });
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.35)', zIndex: 1000 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 460,
        background: '#fff', boxShadow: '-4px 0 24px rgba(15,23,42,.12)',
        zIndex: 1001, display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--c-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Match Contact</div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--c-text-3)', lineHeight: 1, padding: 0 }}>×</button>
          </div>
          <div style={{ marginTop: 10, background: '#f8fafc', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>{item.name || item.email}</div>
            {item.name && <div style={{ fontSize: 12, color: 'var(--c-text-2)', marginTop: 1 }}>{item.email}</div>}
            <div style={{ fontSize: 12, color: 'var(--c-text-2)', marginTop: 2 }}>
              {item.firm_guess || domain}
              {matchedFirm && <span style={{ color: '#1d4ed8', marginLeft: 6 }}>· likely <strong>{matchedFirm.firm_name}</strong></span>}
            </div>
          </div>
        </div>

        {/* Search */}
        <div style={{ padding: '14px 20px 10px', borderBottom: '1px solid var(--c-border)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-text-2)', marginBottom: 6 }}>
            Search existing contacts to link this email:
          </div>
          <div style={{ position: 'relative' }}>
            <input
              ref={searchRef}
              value={query}
              onChange={onQueryChange}
              placeholder="Name, company, or email…"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '8px 32px 8px 10px', fontSize: 13,
                border: '1px solid var(--c-border)', borderRadius: 8, outline: 'none',
              }}
            />
            {searching && <div className="spinner" style={{ width: 14, height: 14, position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }} />}
          </div>
          {!query && matchedFirm && (
            <div style={{ fontSize: 11.5, color: 'var(--c-text-3)', marginTop: 6 }}>
              Tip: try searching "<strong>{matchedFirm.firm_name}</strong>"
            </div>
          )}
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 20px' }}>
          {results === null && !query && (
            <div style={{ fontSize: 13, color: 'var(--c-text-3)', textAlign: 'center', marginTop: 40 }}>
              Type a name or company to search
            </div>
          )}
          {results !== null && results.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--c-text-3)', textAlign: 'center', marginTop: 40 }}>
              No contacts found for "{query}"<br />
              <span style={{ fontSize: 12 }}>Try a different name, or add as a new contact below.</span>
            </div>
          )}
          {(results ?? []).map((c) => {
            const noEmail    = !c.email || c.email.trim() === '';
            const isSelected = selected?.contact_id === c.contact_id;
            return (
              <div
                key={c.contact_id}
                onClick={() => setSelected(c)}
                style={{
                  padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 6,
                  border: `1.5px solid ${isSelected ? 'var(--c-primary)' : 'var(--c-border)'}`,
                  background: isSelected ? '#eff6ff' : '#fff', position: 'relative',
                }}
              >
                {noEmail && (
                  <span style={{
                    position: 'absolute', top: 8, right: 10,
                    fontSize: 10, fontWeight: 700, color: '#92400e',
                    background: '#fef3c7', borderRadius: 99, padding: '1px 6px',
                  }}>no email</span>
                )}
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{c.name || '(no name)'}</div>
                <div style={{ fontSize: 12, color: 'var(--c-text-2)', marginTop: 2 }}>
                  {noEmail
                    ? <span style={{ color: 'var(--c-text-3)', fontStyle: 'italic' }}>no email on record</span>
                    : c.email}
                  {c.firm_name && <span style={{ marginLeft: 6, color: 'var(--c-text-3)' }}>· {c.firm_name}</span>}
                </div>
                {c.previous_email && (
                  <div style={{ fontSize: 11, color: 'var(--c-text-3)', marginTop: 2 }}>
                    previously {c.previous_email}
                    {c.previous_firm_name && ` · ${c.previous_firm_name}`}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--c-border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {linkError && <div style={{ fontSize: 12, color: 'var(--c-red)' }}>{linkError}</div>}
          {linked && <div style={{ fontSize: 12, color: 'var(--c-green)', fontWeight: 600 }}>✓ Email linked successfully</div>}
          <button
            className="btn btn--primary"
            disabled={!selected || linking || linked}
            onClick={doLink}
            style={{ width: '100%' }}
          >
            {linking ? 'Linking…' : linked ? 'Linked ✓' : selected ? `Link email → ${selected.name || selected.contact_id}` : 'Select a contact above to link'}
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, height: 1, background: 'var(--c-border)' }} />
            <span style={{ fontSize: 11, color: 'var(--c-text-3)' }}>or</span>
            <div style={{ flex: 1, height: 1, background: 'var(--c-border)' }} />
          </div>
          <button className="btn btn--ghost" onClick={doAddNew} style={{ width: '100%' }}>
            + Add as new contact{matchedFirm ? ` at ${matchedFirm.firm_name}` : ''}
          </button>
        </div>
      </div>
    </>
  );
}
